import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection 
} from '@/components/WorkbookPage';
import { Home, Calculator, AlertCircle } from 'lucide-react';

export const Rule10Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule10 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule10', { ...data, [field]: value });
  };
  
  return (
    <WorkbookPage pageId="rule-10" prevPage="rule-9" nextPage="rule-11">
      <RuleHeader ruleNumber="10" title="Housing Decisions" section="growth" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Housing is usually your biggest expense. Making smart housing decisions—whether renting 
        or buying—can mean the difference between financial stress and stability. This rule 
        helps you understand affordability, hidden costs, and when each option makes sense.
      </Paragraph>
      
      <SectionTitle>The Affordability Rule</SectionTitle>
      
      <div className="my-6 bg-primary/5 border border-primary/20 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calculator className="h-6 w-6 text-primary" />
          <h3 className="font-semibold">The 28/36 Rule</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-3xl font-bold text-primary mb-1">28%</p>
            <p className="text-sm text-muted-foreground">
              Housing costs should be no more than <strong>28%</strong> of your gross monthly income
            </p>
          </div>
          <div>
            <p className="text-3xl font-bold text-primary mb-1">36%</p>
            <p className="text-sm text-muted-foreground">
              Total debt payments should be no more than <strong>36%</strong> of your gross monthly income
            </p>
          </div>
        </div>
        <div className="mt-4 bg-card rounded-lg p-3">
          <p className="text-sm">
            <strong>Example:</strong> Income $4,000/month → Housing should be under $1,120 (28%)
          </p>
        </div>
      </div>
      
      <Callout type="info">
        <strong>More conservative approach:</strong> Aim for 25% or less of your take-home pay 
        for housing. This gives you more breathing room for savings and unexpected expenses.
      </Callout>
      
      <SectionTitle>Rent vs. Own</SectionTitle>
      
      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="border border-border rounded-lg p-5">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Home className="h-4 w-4" />
            Renting
          </h4>
          <div className="space-y-2 text-sm">
            <p><strong>Pros:</strong></p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Flexibility to move</li>
              <li>No maintenance costs</li>
              <li>Lower upfront costs</li>
              <li>Predictable monthly costs</li>
            </ul>
            <p className="mt-3"><strong>Cons:</strong></p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>No equity building</li>
              <li>Rent can increase</li>
              <li>Less control over space</li>
            </ul>
          </div>
        </div>
        
        <div className="border border-border rounded-lg p-5">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Home className="h-4 w-4" />
            Buying
          </h4>
          <div className="space-y-2 text-sm">
            <p><strong>Pros:</strong></p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Building equity (ownership)</li>
              <li>Potential appreciation</li>
              <li>Stable payment (fixed mortgage)</li>
              <li>Control over your space</li>
            </ul>
            <p className="mt-3"><strong>Cons:</strong></p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Large upfront costs</li>
              <li>Responsible for ALL repairs</li>
              <li>Less flexibility to move</li>
              <li>Hidden ongoing costs</li>
            </ul>
          </div>
        </div>
      </div>
      
      <SectionTitle>Hidden Costs of Homeownership</SectionTitle>
      
      <div className="my-6 bg-warning/5 border border-warning/20 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-3">Beyond the mortgage payment:</p>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              <div>• Property taxes ($1,000-$10,000+/year)</div>
              <div>• Homeowner's insurance</div>
              <div>• Private Mortgage Insurance (PMI)</div>
              <div>• HOA fees (if applicable)</div>
              <div>• Maintenance (1-2% of home value/year)</div>
              <div>• Utilities (often higher than renting)</div>
              <div>• Repairs (roof, HVAC, appliances)</div>
              <div>• Lawn care/landscaping</div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              <strong>Rule of thumb:</strong> Budget 1-2% of your home's value annually for maintenance 
              and repairs. On a $200,000 home, that's $2,000-$4,000/year.
            </p>
          </div>
        </div>
      </div>
      
      <ExampleBox title="When Renting Makes More Sense">
        <ul className="space-y-2 text-sm">
          <li>• You might move in the next 3-5 years</li>
          <li>• You don't have emergency savings yet</li>
          <li>• You have high-interest debt to pay off</li>
          <li>• Home prices in your area are very high relative to rent</li>
          <li>• You value flexibility and low maintenance</li>
        </ul>
      </ExampleBox>
      
      <ExampleBox title="When Buying Might Make Sense">
        <ul className="space-y-2 text-sm">
          <li>• You plan to stay 5+ years</li>
          <li>• You have a stable income</li>
          <li>• You have savings for down payment AND emergencies</li>
          <li>• You can afford all the hidden costs</li>
          <li>• You're ready for maintenance responsibility</li>
        </ul>
      </ExampleBox>
      
      <StepList steps={[
        "Calculate 28% of your gross monthly income—this is your max housing budget.",
        "For a more conservative approach, use 25% of take-home pay.",
        "If renting: Keep security deposit and first/last month's rent in savings.",
        "If considering buying: Save at least 10-20% for down payment.",
        "Don't forget to budget for hidden costs beyond the mortgage.",
        "Have 3-6 months emergency fund BEFORE buying."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Housing Worksheet</h3>
        
        <div className="space-y-6">
          <WorksheetInput
            label="Current housing status:"
            placeholder="Renting, own, living with family, etc."
            value={data.housingStatus || ''}
            onChange={(v) => updateData('housingStatus', v)}
          />
          
          <WorksheetInput
            label="Current monthly housing cost:"
            placeholder="$___"
            value={data.monthlyHousingCost || ''}
            onChange={(v) => updateData('monthlyHousingCost', v)}
          />
          
          <WorksheetInput
            label="My gross monthly income:"
            placeholder="$___"
            value={data.grossIncome || ''}
            onChange={(v) => updateData('grossIncome', v)}
          />
          
          <div className="bg-secondary rounded-lg p-4">
            <p className="text-sm font-medium mb-2">Affordability check:</p>
            <p className="text-sm text-muted-foreground">
              28% of gross income = max housing: $____
            </p>
            <p className="text-sm text-muted-foreground">
              Is my current housing within this? Yes / No
            </p>
          </div>
          
          <WorksheetInput
            label="My housing goals (stay put, downsize, save for down payment, etc.):"
            placeholder="What do you want to achieve?"
            value={data.housingGoals || ''}
            onChange={(v) => updateData('housingGoals', v)}
            multiline
          />
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I understand the 28/36 affordability rule
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I know the pros/cons of renting vs. owning
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I'm aware of hidden costs of homeownership
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Housing should be ≤28% of gross income",
        "Renting offers flexibility; owning builds equity",
        "Hidden costs of owning: taxes, insurance, maintenance",
        "Have emergency fund before buying",
        "Plan to stay 5+ years if buying"
      ]} />
      
      <AffirmationBox>
        I make housing decisions based on my real financial situation, not pressure or comparison.
      </AffirmationBox>
      
      <OptionalSection>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="What does 'home' mean to you?"
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
